import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// ¿Es la llamada de preview del dashboard (limit:1)? Fuente única para que el
// reducer pending y el fulfilled no puedan divergir.
const esPreview = (arg) => ((arg && arg.limit) || 20) === 1

// Devuelve el más reciente por fecha entre dos circulares (para `ultima`).
const masReciente = (a, b) => {
  if (!a) return b || null
  if (!b) return a
  return new Date(b.fecha).getTime() > new Date(a.fecha).getTime() ? b : a
}

// Lista de circulares (solo metadata; el PDF se pide aparte por /:id/archivo)
export const fetchCirculares = createAsyncThunk(
  'circulares/fetchCirculares',
  async (params = {}, { rejectWithValue }) => {
    try {
      const page = params.page || 1
      const queryParams = new URLSearchParams({
        page,
        limit: params.limit || 20,
        search: params.search || '',
        tipo: params.tipo || '',
        orden: params.orden || '',
      }).toString()
      const response = await api.get(`/circulares?${queryParams}`)
      return {
        data: response.data.data,
        pagination: response.data.pagination,
        page,
        // limit:1 se usa para el preview del dashboard: no debe pisar la lista completa
        soloPreview: esPreview(params),
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cargar circulares')
    }
  }
)

// Métricas para la tira de KPIs (agregados del servidor, no del page cargado)
export const fetchCircularesStats = createAsyncThunk(
  'circulares/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/circulares/stats')
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cargar estadísticas')
    }
  }
)

// Crear (FormData con el PDF). El componente arma el FormData.
export const createCircular = createAsyncThunk(
  'circulares/createCircular',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/circulares', formData)
      return response.data.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details ||
          error.response?.data?.message ||
          'Error al publicar la circular'
      )
    }
  }
)

// Editar (FormData; el PDF es opcional al editar)
export const updateCircular = createAsyncThunk(
  'circulares/updateCircular',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/circulares/${id}`, formData)
      return response.data.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details ||
          error.response?.data?.message ||
          'Error al actualizar la circular'
      )
    }
  }
)

export const deleteCircular = createAsyncThunk(
  'circulares/deleteCircular',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/circulares/${id}`)
      return id
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al eliminar la circular')
    }
  }
)

const initialState = {
  circulares: [],
  ultima: null, // documento más reciente por fecha (preview del dashboard, orden=reciente)
  stats: { total: 0, destacadas: 0, esteMes: 0, tipos: 0, porTipo: [] },
  pagination: { current: 1, pages: 1, total: 0, hasNext: false, hasPrev: false },
  loading: false,
  statsLoading: false,
  loadingMore: false, // "cargar más" (page > 1): no debe blanquear la lista
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  error: null,
  createError: null,
  updateError: null,
  deleteError: null,
}

const circularesSlice = createSlice({
  name: 'circulares',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
      state.createError = null
      state.updateError = null
      state.deleteError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCirculares.pending, (state, action) => {
        const a = action.meta.arg || {}
        if (esPreview(a)) {
          // el preview (limit:1) no toca los flags de carga de la lista
        } else if ((a.page || 1) > 1) {
          state.loadingMore = true
        } else {
          state.loading = true
        }
        state.error = null
      })
      .addCase(fetchCirculares.fulfilled, (state, action) => {
        if (action.payload.soloPreview) {
          // preview del dashboard: pedido con orden=reciente → el más actual por fecha
          state.ultima = action.payload.data?.[0] || null
        } else {
          state.loading = false
          state.loadingMore = false
          // page > 1 → "cargar más" (append); page 1 → reemplaza la lista
          state.circulares =
            action.payload.page > 1
              ? state.circulares.concat(action.payload.data)
              : action.payload.data
          state.pagination = action.payload.pagination
        }
      })
      .addCase(fetchCirculares.rejected, (state, action) => {
        state.loading = false
        state.loadingMore = false
        state.error = action.payload
      })

      .addCase(fetchCircularesStats.pending, (state) => {
        state.statsLoading = true
      })
      .addCase(fetchCircularesStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.stats = action.payload
      })
      .addCase(fetchCircularesStats.rejected, (state) => {
        state.statsLoading = false
      })

      .addCase(createCircular.pending, (state) => {
        state.createLoading = true
        state.createError = null
      })
      .addCase(createCircular.fulfilled, (state, action) => {
        state.createLoading = false
        state.circulares.unshift(action.payload)
        // solo pasa a ser `ultima` si es más reciente por fecha (no por ser el último creado)
        state.ultima = masReciente(state.ultima, action.payload)
        if (state.pagination) state.pagination.total = (state.pagination.total || 0) + 1
      })
      .addCase(createCircular.rejected, (state, action) => {
        state.createLoading = false
        state.createError = action.payload
      })

      .addCase(updateCircular.pending, (state) => {
        state.updateLoading = true
        state.updateError = null
      })
      .addCase(updateCircular.fulfilled, (state, action) => {
        state.updateLoading = false
        const i = state.circulares.findIndex((c) => c.id === action.payload.id)
        if (i !== -1) state.circulares[i] = action.payload
        // si editaron la que era `ultima`, sincronizar (el dashboard re-deriva por fecha al montar)
        if (state.ultima?.id === action.payload.id) state.ultima = action.payload
      })
      .addCase(updateCircular.rejected, (state, action) => {
        state.updateLoading = false
        state.updateError = action.payload
      })

      .addCase(deleteCircular.pending, (state) => {
        state.deleteLoading = true
        state.deleteError = null
      })
      .addCase(deleteCircular.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.circulares = state.circulares.filter((c) => c.id !== action.payload)
        // si borraron la `ultima`, queda null hasta el próximo preview del dashboard
        if (state.ultima?.id === action.payload) state.ultima = null
        if (state.pagination && state.pagination.total > 0) state.pagination.total -= 1
      })
      .addCase(deleteCircular.rejected, (state, action) => {
        state.deleteLoading = false
        state.deleteError = action.payload
      })
  },
})

export const selectCirculares = (state) => state.circulares?.circulares || []
export const selectUltimaCircular = (state) => state.circulares?.ultima || null
export const selectCircularesLoading = (state) => state.circulares?.loading || false

export const { clearError } = circularesSlice.actions
export default circularesSlice.reducer
